import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useState } from 'react';

import { ServiceNodeAllocationType } from '../../../../../@types/allocation.type';
import { LocationNodeData } from '../../../../../@types/live-search.type';
import { useSearchAllocationNodes } from '../../../../../queries/live-search-query';
import { useGetCurrentUser } from '../../../../../queries/user-query';
import { displayNodeTypeName } from '../../../../../utils/node-type';

export interface AllocationDialogProps {
  open: boolean;
  onClose: () => void;
  currentTechnician: string;
  onSubmit: (nodeId: number, allocationTypeCode: string) => void;
  submitting: boolean;
}

export const AllocationDialog: React.FC<AllocationDialogProps> = ({
  open,
  onClose,
  currentTechnician,
  onSubmit,
  submitting,
}) => {
  const theme = useTheme();
  const searchMutation = useSearchAllocationNodes();
  const currentUserQuery = useGetCurrentUser();
  const currentUserNodeId = currentUserQuery.data?.siblingNodeId ?? 0;
  const currentUserName =
    [currentUserQuery.data?.firstName, currentUserQuery.data?.lastName].filter(Boolean).join(' ') ||
    currentUserQuery.data?.siblingNodeName ||
    '';
  const canTake = currentUserNodeId > 0;
  const [selectedNode, setSelectedNode] = useState<LocationNodeData | null>(null);
  const [allocationType, setAllocationType] = useState(ServiceNodeAllocationType.Primary);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<LocationNodeData[]>([]);

  useEffect(() => {
    if (open) {
      setSelectedNode(null);
      setAllocationType(ServiceNodeAllocationType.Primary);
      setInputValue('');
      setOptions([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open || inputValue.length < 2) {
      setOptions([]);
      return;
    }
    const timer = setTimeout(() => {
      searchMutation
        .mutateAsync(inputValue)
        .then(setOptions)
        .catch(() => setOptions([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue, open]);

  const handleSubmit = useCallback(() => {
    if (selectedNode) onSubmit(selectedNode.id, allocationType);
  }, [selectedNode, allocationType, onSubmit]);

  const handleTake = useCallback(() => {
    if (canTake && !submitting) onSubmit(currentUserNodeId, ServiceNodeAllocationType.Primary);
  }, [canTake, submitting, currentUserNodeId, onSubmit]);

  const activateOnKey = (callback: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  };

  const optionSx = (active: boolean, disabled = false) => ({
    px: 1.5,
    py: 1,
    borderRadius: 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
    textAlign: 'center' as const,
    border: '1px solid',
    fontSize: '12px',
    fontWeight: 500,
    borderColor: active ? theme.palette.v4.accent : theme.palette.v4.border,
    bgcolor: active ? theme.palette.v4.accent : 'transparent',
    color: active ? '#fff' : theme.palette.text.secondary,
    opacity: disabled ? 0.5 : 1,
    '&:hover': disabled ? undefined : { borderColor: theme.palette.v4.accent, opacity: 0.85 },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem' }}>Allocate Technician</DialogTitle>
      <DialogContent>
        {currentTechnician && (
          <Typography sx={{ fontSize: '12px', color: 'text.secondary', mb: 2 }}>
            Current: <strong>{currentTechnician}</strong>
          </Typography>
        )}
        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
          Allocation Type
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Box
            role="button"
            tabIndex={0}
            aria-pressed={allocationType === ServiceNodeAllocationType.Primary}
            sx={optionSx(allocationType === ServiceNodeAllocationType.Primary)}
            onClick={() => setAllocationType(ServiceNodeAllocationType.Primary)}
            onKeyDown={activateOnKey(() => setAllocationType(ServiceNodeAllocationType.Primary))}
          >
            Primary
          </Box>
          <Box
            role="button"
            tabIndex={0}
            aria-pressed={allocationType === ServiceNodeAllocationType.Secondary}
            sx={optionSx(allocationType === ServiceNodeAllocationType.Secondary)}
            onClick={() => setAllocationType(ServiceNodeAllocationType.Secondary)}
            onKeyDown={activateOnKey(() => setAllocationType(ServiceNodeAllocationType.Secondary))}
          >
            Secondary
          </Box>
          <Box
            role="button"
            tabIndex={canTake && !submitting ? 0 : -1}
            aria-disabled={!canTake || submitting}
            title={
              canTake
                ? `Assign ${currentUserName || 'yourself'} as primary`
                : 'No personal node linked to your account'
            }
            sx={optionSx(false, !canTake || submitting)}
            onClick={handleTake}
            onKeyDown={activateOnKey(() => {
              if (canTake && !submitting) handleTake();
            })}
          >
            Take
          </Box>
        </Box>
        <Autocomplete
          size="small"
          options={options}
          getOptionLabel={(opt) => opt.name}
          filterOptions={(x) => x}
          inputValue={inputValue}
          onInputChange={(_, val) => setInputValue(val)}
          onChange={(_, node) => setSelectedNode(node)}
          loading={searchMutation.isPending}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search technician / depot"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- dialog search field: focus moves into dialog when opened
              autoFocus
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px' } }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={`${option.id}-${option.code}`}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>{option.name}</Typography>
                <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>
                  {displayNodeTypeName(option.nodeTypeName)} &middot; {option.code}
                </Typography>
              </Box>
            </li>
          )}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!selectedNode || submitting}
          sx={{
            textTransform: 'none',
            bgcolor: 'v4.accent',
            '&:hover': { bgcolor: 'v4.accent', opacity: 0.9 },
          }}
        >
          {submitting ? <CircularProgress size={16} sx={{ color: 'common.white' }} /> : 'Allocate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
